import { api } from "@/services/api";
import {
    ASSET_MARGIN_REPORT,
    BALANCE_UPDATE,
    ORDER_EVENT,
    POSITION_MARGIN_REPORT,
    POSITION_UPDATE,
    TRADE_EVENT,
    TRANSFER_EVENT,
} from "@/services/consts";

/** Feed type for dropcopy history (must match API segment). */
export type BlotterFeedType =
    | typeof ORDER_EVENT
    | typeof POSITION_UPDATE
    | typeof BALANCE_UPDATE
    | typeof ASSET_MARGIN_REPORT
    | typeof POSITION_MARGIN_REPORT
    | typeof TRADE_EVENT
    | typeof TRANSFER_EVENT;

/** Params for initial fetch or fetch by start date / pagination token. */
export type GetBlotterDropcopyDataParams = {
    account_id: string;
    feed_type: BlotterFeedType;
    limit?: number;
    direction?: "asc" | "desc";
    start_ms?: string | null;
    pagination_token?: string | null;
};

/** Params for bound dates (date picker min/max). */
export type GetBoundDatesParams = {
    account_id: string;
    feed_type?: BlotterFeedType;
};

/** API response shape for dropcopy history (data array + optional token). */
export type BlotterDropcopyResponse = {
    data: any[];
    token?: string;
};

export const blotterApi = api.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Fetch dropcopy history for an account/feed.
         * Use for: initial load, load by start date, load more (pagination token).
         * Use the lazy hook and pass start_ms or pagination_token when needed.
         */
        getBlotterDropcopyData: builder.query<
            BlotterDropcopyResponse,
            GetBlotterDropcopyDataParams
        >({
            query: ({
                account_id,
                feed_type,
                limit = 500,
                direction = "desc",
                start_ms,
                pagination_token,
            }) => {
                const params = new URLSearchParams();

                if (pagination_token) {
                    params.append("token", pagination_token);
                } else {
                    params.append("limit", limit.toString());
                    params.append("direction", direction);
                    if (start_ms) {
                        params.append("start_ms", start_ms);
                    }
                }

                return `/api/dropcopy/v1/history/${account_id}/${feed_type}?${params.toString()}`;
            },
            providesTags: (result, _err, arg) =>
                result
                    ? [{ type: "BlotterDropcopy", id: `${arg.account_id}-${arg.feed_type}` }]
                    : [],
        }),

        /**
         * Fetch date bounds for the date picker (metadata only).
         * Call after you have data to get first_ts; max is usually from your first row.
         */
        getBoundDates: builder.query<
            { min: string; max: string; first_ts?: string },
            GetBoundDatesParams
        >({
            query: ({ account_id, feed_type = "order_event" }) =>
                `/api/dropcopy/v1/history/${account_id}/${feed_type}?metadata=true`,
            transformResponse: (response: { first_ts?: string }) => ({
                min: response?.first_ts ?? "",
                max: response?.first_ts ?? "",
                first_ts: response?.first_ts,
            }),
        }),
    }),
});

/**
 * Regular vs lazy query hooks:
 *
 * - useGetXxxQuery(args): Subscribes to the cache and fetches automatically when
 *   the component renders with those args (and when args change). Use when you
 *   always want this data whenever the screen is visible and args are set.
 *
 * - useLazyGetXxxQuery(): Returns a trigger function; no request runs until you
 *   call it (e.g. in an effect or click handler). Use when you want to fetch only
 *   at specific times (initial load after account is set, "Load more", date picker).
 */
export const {
    useGetBlotterDropcopyDataQuery,
    useLazyGetBlotterDropcopyDataQuery,
    useGetBoundDatesQuery,
    useLazyGetBoundDatesQuery,
} = blotterApi;